import { faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Session } from '@pickleballinc/lib/session';
import { Tooltip, TooltipContent, TooltipTrigger } from '@pickleballinc/react-ui';
import clsx from 'clsx';

import { PlayerData } from '@/types/players.types';

interface BoxValueProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
  value: React.ReactNode;
  shouldBlur?: boolean;
  placeholder?: React.ReactNode;
}

const BoxValue = ({ label, value, className, shouldBlur, placeholder, ...rest }: BoxValueProps) => {
  const boxClasses = clsx('h-auto items-center py-2 text-sm font-semibold sm:text-base', className);

  return (
    <div className={boxClasses} {...rest}>
      <div className='text-sm font-normal'>{label}</div>
      {!value ? placeholder : <span className={clsx(shouldBlur && 'blur-sm')}>{value}</span>}
    </div>
  );
};

interface ProfileInfoBoxProps {
  playerData: PlayerData;
  hasBorder?: boolean;
  isDuprActive?: boolean;
  user?: Session;
}

export const ProfileInfoBox = ({
  playerData,
  hasBorder,
  isDuprActive,
  user,
}: ProfileInfoBoxProps) => {
  const boxClasses = clsx('flex w-full flex-col divide-y rounded-md py-2', {
    'border border-white px-2': hasBorder,
  });
  const shouldBlur = !user?.token && playerData.proLevelId !== 5;
  const duprId = playerData?.duprId || '-';

  return (
    <div className={boxClasses}>
      <div className='flex flex-row items-stretch divide-x'>
        <BoxValue label='Gender' value={playerData.gender || ''} className={'w-[90px] shrink-0'} />
        <BoxValue
          label='Age'
          value={playerData.age}
          placeholder={
            <Tooltip placement='bottom'>
              <TooltipTrigger asChild>
                <FontAwesomeIcon icon={faEyeSlash} />
              </TooltipTrigger>
              <TooltipContent className='w-52 rounded bg-black/75 p-2 text-center text-sm text-white'>
                Player age hidden by player
              </TooltipContent>
            </Tooltip>
          }
          className='w-[60px] shrink-0 pl-2'
          shouldBlur={shouldBlur}
        />
        <BoxValue
          label='Nickname'
          value={playerData.nickname || ''}
          className='hidden flex-1 px-2'
          shouldBlur={shouldBlur}
        />
        <BoxValue
          label='Height'
          value={playerData.height || ''}
          className='flex-1 pl-2'
          shouldBlur={shouldBlur}
        />
        {isDuprActive ? <BoxValue label='DUPR ID' value={duprId} className='flex-1 pl-2' /> : null}
      </div>
      <div className='flex flex-row divide-x'>
        {playerData.turnedPro ? (
          <BoxValue
            label='Turned Pro'
            value={playerData.turnedPro || ''}
            className='w-[90px] shrink-0'
          />
        ) : null}
        <BoxValue
          label='Plays'
          value={playerData.handed || ''}
          className={`flex-1 ${playerData.turnedPro ? 'pl-2' : ''}`}
        />
        <BoxValue
          label='Resides'
          value={playerData.resides}
          placeholder={
            <Tooltip placement='bottom'>
              <TooltipTrigger asChild>
                <FontAwesomeIcon icon={faEyeSlash} />
              </TooltipTrigger>
              <TooltipContent className='w-52 rounded bg-black/75 p-2 text-center text-sm text-white'>
                Player resides hidden by player
              </TooltipContent>
            </Tooltip>
          }
          className='flex-1 pl-2'
          shouldBlur={shouldBlur}
        />
      </div>
    </div>
  );
};
